import React, { useState, useCallback } from 'react';
import './SupportTicketForm.css';

const CATEGORIES = [
  { value: 'shipping', label: 'Shipping' },
  { value: 'product_quality', label: 'Product Quality' },
  { value: 'refund_inquiry', label: 'Refund Inquiry' },
  { value: 'order_issue', label: 'Order Issue' },
  { value: 'general', label: 'General' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

function SupportTicketForm({ apiBase, getToken, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1: Find/Add Customer, 2: Ticket Details
  const [form, setForm] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    shopifyOrderNumber: '',
  });
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const searchCustomers = useCallback(async () => {
    if (!customerSearch.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(
        `${apiBase}/api/v1/support/customers/search?q=${encodeURIComponent(customerSearch)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setCustomerResults(data.customers || []);
    } catch (err) {
      setError(err.message);
      setCustomerResults([]);
    } finally {
      setSearching(false);
    }
  }, [apiBase, getToken, customerSearch]);

  const selectCustomer = (customer) => {
    setForm((prev) => ({
      ...prev,
      customerName: customer.displayName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      shopifyCustomerId: customer.id || '',
      shopifyOrderNumber: customer.associatedOrderNumber || prev.shopifyOrderNumber || '',
    }));
    setCustomerResults([]);
    setCustomerSearch('');
    setError(null);
    setStep(2);
  };

  const handleNextStep = () => {
    if (!form.customerName.trim()) {
      setError('Customer Name is required');
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) {
      setError('Subject is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBase}/api/v1/support/tickets`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || 'Failed to create ticket');
      }
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-modal-overlay" onClick={onClose}>
      <div className="support-modal" onClick={(e) => e.stopPropagation()}>
        <div className="support-modal-header">
          <h2>New Support Ticket</h2>
          <button className="support-modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="support-error">{error}</div>}

        <div className="ticket-form-steps">
          <span>Step {step} of 2</span>
          <span>{step === 1 ? 'Find / Add Customer' : 'Ticket Details'}</span>
        </div>

        {step === 1 ? (
          <div>
            <div className="support-form-group">
              <label>Search Shopify Customer</label>
              <div className="ticket-customer-search">
                <input
                  type="text"
                  className="support-input"
                  placeholder="Search by name, email, phone, or order number..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchCustomers())}
                />
                <button type="button" className="support-btn primary small" onClick={searchCustomers} disabled={searching}>
                  {searching ? '...' : 'Find'}
                </button>
              </div>
              {customerResults.length > 0 && (
                <div className="ticket-customer-results">
                  {customerResults.map((c) => (
                    <div key={c.id} className="ticket-customer-result" onClick={() => selectCustomer(c)}>
                      <span className="ticket-customer-result-name">
                        {c.displayName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown'}
                        {c.associatedOrderNumber && <span className="ticket-customer-result-order"> (Order: {c.associatedOrderNumber})</span>}
                      </span>
                      <span className="ticket-customer-result-email">{c.email || c.phone || 'No email/phone'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="ticket-form-section-label">Or Enter Customer Details Manually</div>

            <div className="support-form-row">
              <div className="support-form-group">
                <label>Customer Name *</label>
                <input type="text" className="support-input" value={form.customerName} onChange={handleChange('customerName')} placeholder="e.g. John Doe" />
              </div>
              <div className="support-form-group">
                <label>Email</label>
                <input type="email" className="support-input" value={form.customerEmail} onChange={handleChange('customerEmail')} placeholder="e.g. john@example.com" />
              </div>
            </div>

            <div className="support-form-row">
              <div className="support-form-group">
                <label>Phone</label>
                <input type="tel" className="support-input" value={form.customerPhone} onChange={handleChange('customerPhone')} placeholder="e.g. +91 98765 43210" />
              </div>
              <div className="support-form-group">
                <label>Shopify Order #</label>
                <input type="text" className="support-input" value={form.shopifyOrderNumber} onChange={handleChange('shopifyOrderNumber')} placeholder="e.g. #12345" />
              </div>
            </div>

            <div className="support-modal-actions">
              <button type="button" className="support-btn" onClick={onClose}>Cancel</button>
              <button type="button" className="support-btn primary" onClick={handleNextStep} disabled={!form.customerName.trim()}>
                Next &rarr;
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="ticket-form-customer-summary">
              <div className="summary-details">
                <strong>👤 {form.customerName}</strong>
                <span>
                  {form.customerEmail && `📧 ${form.customerEmail}`}
                  {form.customerPhone && `  📞 ${form.customerPhone}`}
                  {form.shopifyOrderNumber && `  🛒 Order ${form.shopifyOrderNumber}`}
                </span>
              </div>
              <button type="button" className="support-btn small secondary" onClick={() => setStep(1)}>
                Edit Customer
              </button>
            </div>

            <div className="support-form-group">
              <label>Subject *</label>
              <input
                type="text"
                className="support-input"
                value={form.subject}
                onChange={handleChange('subject')}
                placeholder="Brief description of the issue"
                required
              />
            </div>

            <div className="support-form-row">
              <div className="support-form-group">
                <label>Category</label>
                <select className="support-select" value={form.category} onChange={handleChange('category')}>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="support-form-group">
                <label>Priority</label>
                <select className="support-select" value={form.priority} onChange={handleChange('priority')}>
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="support-form-group">
              <label>Description</label>
              <textarea
                className="support-textarea"
                value={form.description}
                onChange={handleChange('description')}
                placeholder="Detailed description..."
              />
            </div>

            <div className="support-modal-actions">
              <button type="button" className="support-btn" onClick={() => setStep(1)}>&larr; Back</button>
              <button type="submit" className="support-btn primary" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Ticket'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default SupportTicketForm;
